import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from 'react';

import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Today as TodayIcon,
  TransferWithinAStation as TransferWithinAStationIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import StatCard from './admindashboard/StatCard';
import { APP_LINK } from '../common/menu/menuItems/menuList';
import ReusableTable from '../common/table/CommonTable';
import { fetchAdminCountRequest, fetchStaffCountRequest } from '../redux/dashboard/dashboardAction';
import {
  fetchHistoryPatients,
  fetchPatients,
} from '../redux/patient/patientAction';
import { fetchSecurityOfficers } from '../redux/secuityOfficer/securityAction';
import { fetchTransferPatient } from '../redux/TransferPatient/transferPatientAction';

const StaffDashboard = () => {
  const dispatch = useDispatch();
    const navigate = useNavigate()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processedData, setProcessedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  const securityOfficerList = securityOfficerState?.data?.data || [];

  const patientList = useSelector(
    (state) => state?.patients?.fetchListPatients || {}
  );
  const adminState = useSelector(
    (state) => state?.dashboard?.fetchStaffCount?.count || {}
  );

  const historyState = useSelector(
    (state) => state?.patients?.historyPatient || {}
  );
  const historyData = historyState?.data?.data || [];

  const transferState = useSelector(
    (state) => state?.transferPatient?.fetchListTransferPatient || {}
  );

  const adminStates = useSelector(
    (state) => state?.dashboard?.fetchAdminCount || {}
  );

  const columnSecurity = [
    { id: 'security_serial_no', label: 'Guard ID', sortable: true },
    { id: 'security_gaurd_name', label: 'Name', sortable: true },
    { id: 'phone_number', label: 'Phone Number', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? 'Active' : 'Inactive'}
          color={value === 1 ? 'success' : 'warning'}
        />
      ),
    },
  ];

  const page = 1;
  const rowsPerPage = 10;
  const searchTerm = "";

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        pageNo: page,
        pagesize: rowsPerPage,
        pagination_required: false,
        search: searchTerm,
      };
      await dispatch(fetchPatients(currentPage));
      await dispatch(fetchStaffCountRequest(currentPage));
      await dispatch(fetchAdminCountRequest(currentPage));
      await dispatch(fetchSecurityOfficers(payload));
      await dispatch(fetchHistoryPatients(currentPage));
      await dispatch(fetchTransferPatient(currentPage));
      setLoading(false);
    };
    fetchData();
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (patientList.data && Array.isArray(patientList.data.data)) {
      const formattedData = patientList.data.data.map((patient) => ({
        id: patient.patient_serial_no,
        name: patient.patient_name,
        age: patient.age,
        status: patient.status || 'Active',
      }));
      setData(formattedData);
    } else {
      setData([]);
    }
  }, [patientList]);

  useEffect(() => {
    if (historyData.length > 0) {
      const processedData = historyData.map((item) => ({
        attendee: item.Attender?.attender_name || 'Unknown',
        attender_serial_no: item.Attender?.attender_serial_no || 'Unknown',
        status: item.attender_status === 2 ? 'In' : 'Out',
      }));

      setFilteredData(processedData);
      setLoading(false);
    }
  }, [historyData]);

  useEffect(() => {
    if (Array.isArray(transferState?.data?.data)) {
      const processed = transferState.data.data.map((item, index) => ({
        serialNo: index + 1,
        id: item.id,
        patientName: item.Patient?.patient_name || 'N/A',
        ipNumber: item.Patient?.ip_number || 'N/A',
        phoneNumber: item.Patient?.phone || 'N/A',
        staffName: item.Staff?.staff_name || 'N/A',
        oldBlock: item.old_Block?.block_name || 'N/A',
        oldFloor: item.old_Floor?.floor_name || 'N/A',
        oldWard: item.old_Ward?.ward_name || 'N/A',
        newBlock: item.new_Block?.block_name || 'N/A',
        newFloor: item.new_Floor?.floor_name || 'N/A',
        newWard: item.new_Ward?.ward_name || 'N/A',
        transferDate: new Date(item.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        reason: item.reason || 'N/A',
      }));
      setProcessedData(processed);
      setFilteredData(processed);
    } else {
      setProcessedData([]);
      setFilteredData([]);
    }
  }, [transferState]);

  const columns = [
    { id: 'serialNo', label: 'S.No', sortable: true },
    { id: 'patientName', label: 'Patient Name', sortable: true },
    { id: 'phoneNumber', label: 'Phone Number', sortable: true },
    {
      id: 'oldBlock',
      label: 'Old Block',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="primary"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(33, 150, 243, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'oldFloor',
      label: 'Old Floor',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="primary"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(33, 150, 243, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'oldWard',
      label: 'Old Ward',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="primary"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(33, 150, 243, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newBlock',
      label: 'New Block',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newFloor',
      label: 'New Floor',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newWard',
      label: 'New Ward',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'transferDate',
      label: 'Transfer Date',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
  ];
  
  const TodayStatCard = ({ stat }) => (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 3,
        background: `linear-gradient(135deg, ${stat.color}22 0%, ${stat.color}11 100%)`,
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${stat.color}33`,
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: `0 10px 20px ${stat.color}33`,
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -15,
          right: -15,
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: `${stat.color}22`,
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 700, color: stat.color }}
          >
            {stat.count}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            {stat.title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: stat.color,
            width: 56,
            height: 56,
            color: "white",
          }}
        >
          {stat.icon}
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Divider />
        <Typography
          variant="body2"
          sx={{
            pt: 1,
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Activity Today
        </Typography>
      </Box>
    </Paper>
  );

  const countData = adminStates?.count?.count || {};

  const statistics = {
    totalPatients: adminState?.count?.admissionCount || 0,
    activeAttendees: adminState?.count?.patientShift || 0,
    attendeesIn: adminState?.count?.inAttenderCount || 0,
    attendeesOut: adminState?.count?.outAttenderCount || 0,
    scanToday: adminState?.count?.patientShift || 0,
    todayPatients: countData.todayPatientsCount || 0,
    todayAttenders: countData.todayattenderCount || 0,
  };

  const overviewStats = [
    {
      title: "Currently Registered",
      count: statistics.totalPatients,
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      color: "#673ab7",
    },
    {
      title: "Attendees IN",
      count: statistics.attendeesIn,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#4caf50",
    },
    {
      title: "Attendees OUT",
      count: statistics.attendeesOut,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#ff5722",
    },
     {
      title: "Transfers Today",
      count: statistics.scanToday, 
      icon: <TransferWithinAStationIcon sx={{ fontSize: 40 }} />,
      color: "#ff9800",
    },
  ];

  const activityStats = [
   
    {
      title: "Today's Patients",
      count: statistics.todayPatients,
      icon: <TodayIcon sx={{ fontSize: 40 }} />,
      color: "#e91e63",
    },
    {
      title: "Today's Attenders",
      count: statistics.todayAttenders,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#009688",
    },
  ];

  const patientColumns = [
    { id: 'id', label: 'ID', sortable: true },
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
    { id: 'age', label: 'DOB', sortable: false },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (status) => (
        <Box
          sx={{
            background:
              status === 'IN'
                ? 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)'
                : 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            display: 'inline-block',
            color: 'white',
            fontWeight: 'bold',
            boxShadow:
              status === 'IN'
                ? '0 2px 5px rgba(76, 175, 80, 0.3)'
                : '0 2px 5px rgba(244, 67, 54, 0.3)',
          }}
        >
          {status}
        </Box>
      ),
    },
  ];

  const handleGenerateReport = () => {
    navigate(APP_LINK.DAILYREPORT)
  };

  // Animation variants
  const containerVariant = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        minHeight: '100vh',
        pb: 4,
      }}
    >
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
        }}
      >
        {/* Overview Stats Using StatCard */}
             <motion.div
          variants={containerVariant}
          initial="hidden"
          animate="visible"
        >
        <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              color: "#333",
            }}
          >
            <DashboardIcon sx={{ mr: 1, color: "#2196f3" }} />
            Overview
          </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {overviewStats.map((stat, index) => (
             <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={itemVariant}>
                  <StatCard stat={stat} index={index} />
                </motion.div>
              </Grid>
          ))}
        </Grid>
        </motion.div>

        {/* Activity Stats Using TodayStatCard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
        <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              color: "#333",
            }}
          >
            <TodayIcon sx={{ mr: 1, color: "#e91e63" }} />
            Today's Activity
          </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {activityStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <TodayStatCard stat={stat} />
            </Grid>
          ))}
        </Grid>
        </motion.div>

        {/* Report Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            // startIcon={<DownloadIcon />}
            onClick={handleGenerateReport}
            sx={{
              borderRadius: 2,
              py: 1.2,
              px: 3,
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 176, 255, 0.3)',
              background: 'linear-gradient(45deg, #0288D1 30%, #29B6F6 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #0277BD 30%, #0288D1 90%)',
                boxShadow: '0 6px 14px rgba(0, 176, 255, 0.4)',
                color: 'white',
              }
            }}
          >
            Report
          </Button>
        </Box>

        {/* Recent Patients and Current Security using ReusableTable */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          
   {/* Current Security */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: 'linear-gradient(135deg, #00C853 0%, #64DD17 100%)',
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Recent Patients</Typography>
                  </Box>
                }
                columns={patientColumns}
                data={data.slice(0, 5)}
                headerColor={"#000"}
                withPagination={false}
                withActions={false}
                rowsPerPage={4}
                isLoading={patientList.inProgress}
                customEmptyMessage="No patients registered"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(0, 200, 83, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      backgroundColor: '#E8F5E9',
                    },
                  },
                }}
              />
            </Card>
          </Grid>
          {/* Current Security */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: 'linear-gradient(135deg, #00C853 0%, #64DD17 100%)',
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Current Security</Typography>
                  </Box>
                }
                columns={columnSecurity}
                data={securityOfficerList.slice(0, 5)}
                headerColor={"#000"}
                withPagination={false}
                withActions={false}
                rowsPerPage={4}
                isLoading={securityOfficerState.inProgress}
                customEmptyMessage="No security personnel registered"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(0, 200, 83, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      backgroundColor: '#E8F5E9',
                    },
                  },
                }}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Patient Transfer Activity */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: 'linear-gradient(135deg, #FF9100 0%, #FFCA28 100%)',
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Patient Transfer Activity</Typography>
                  </Box>
                }
                columns={columns}
                data={processedData.slice(0, 5)}
                headerColor={"#000"}
                withPagination={true}
                withActions={false}
                rowsPerPage={5}
                isLoading={transferState.inProgress}
                customEmptyMessage="No recent activity"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(245, 124, 0, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                    },
                  },
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
    
   
  );
};

export default StaffDashboard;
